export interface ChromeState {
  widgetOpen: boolean;
  contactOpen: boolean;
}

export type EscapeFocus = 'contact' | 'launcher' | 'unchanged';

/** Escape closes the contact dialog before it closes the widget. */
export function onEscape(state: ChromeState): ChromeState & { focus: EscapeFocus } {
  if (state.contactOpen) {
    return { widgetOpen: state.widgetOpen, contactOpen: false, focus: 'contact' };
  }
  if (state.widgetOpen) return { widgetOpen: false, contactOpen: false, focus: 'launcher' };
  return { widgetOpen: false, contactOpen: false, focus: 'unchanged' };
}

/** Tab order inside the iframe. Contact stays available without an assistant action. */
export const IFRAME_TAB_ORDER = [
  'language-auto',
  'language-el',
  'language-en',
  'language-fr',
  'contact-open',
  'new-conversation',
  'composer',
  'send',
] as const;

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Keeps Tab cycling inside the contact dialog. Returns a cleanup function. */
export function trapFocus(container: HTMLElement): () => void {
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Tab') return;
    const nodes = [...container.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
      (node) => !node.hasAttribute('disabled') && node.getAttribute('aria-hidden') !== 'true',
    );
    if (nodes.length === 0) {
      event.preventDefault();
      return;
    }
    const first = nodes[0]!;
    const last = nodes[nodes.length - 1]!;
    const active = document.activeElement;
    if (event.shiftKey) {
      if (active === first || !container.contains(active)) {
        event.preventDefault();
        last.focus();
      }
      return;
    }
    if (active === last || !container.contains(active)) {
      event.preventDefault();
      first.focus();
    }
  };
  container.addEventListener('keydown', onKeyDown);
  return () => container.removeEventListener('keydown', onKeyDown);
}
